'use client'

import { ROUTES } from '@/constants/routes'
import { Feature } from '@/types/landing'
import { motion, useTransform } from 'framer-motion'
import { ArrowRight } from 'lucide-react'
import Image from 'next/image'
import { useRouter } from 'next/navigation'
import { getResolvedImageUrl } from '@/utils/image'

export function FeatureCard({
  feature,
  index,
  total,
  t,
  scrollYProgress,
}: {
  feature: Feature
  index: number
  total: number
  t: any
  scrollYProgress: any
}) {
  const router = useRouter()
  const sectionPart = 1 / total
  const start = index * sectionPart
  const y = useTransform(scrollYProgress, [start - 0.15, start], [index === 0 ? 0 : 800, 0])
  const opacity = useTransform(scrollYProgress, [start - 0.15, start], [index === 0 ? 1 : 0, 1])
  const nextStart = (index + 1) * sectionPart
  const scale = useTransform(scrollYProgress, [nextStart - 0.15, nextStart], [1, 0.9])

  // Dimming effect for cards that are covered
  const brightness = useTransform(scrollYProgress, [nextStart - 0.15, nextStart], [1, 0.4])

  return (
    <motion.div
      style={{
        opacity,
        scale,
        y,
        filter: `brightness(${brightness})`,
        zIndex: index + 1,
        position: 'absolute',
        inset: 0,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
      }}
      className="w-full h-full pointer-events-none"
    >
      <div
        className={`group relative w-full pointer-events-auto rounded-[2rem] lg:rounded-[4rem] p-6 lg:p-12 border border-white/10 overflow-hidden bg-light-body transition-all duration-700 hover:border-primary/30`}
      >
        <div
          className={`absolute -top-40 -right-40 w-96 h-96 ${feature.bg} blur-[150px] -z-10 opacity-20 group-hover:opacity-40 transition-opacity duration-1000`}
        />

        <div
          className={`flex flex-col ${index % 2 === 0 ? 'lg:flex-row' : 'lg:flex-row-reverse'} gap-6 lg:gap-20 items-center justify-between h-full`}
        >
          <div className="w-full lg:w-1/2 space-y-4 lg:space-y-8 flex flex-col justify-center text-center lg:text-left">
            <div className="space-y-2 lg:space-y-6">
              <h3 className={`text-2xl sm:text-3xl lg:text-4xl font-bold leading-tight tracking-tight ${feature.color}`}>
                {t(feature.title)}
              </h3>
              <p className="text-sm sm:text-base lg:text-lg text-white/40 leading-relaxed max-w-xl mx-auto lg:mx-0">{t(feature.description)}</p>
            </div>

            <div className="flex flex-wrap gap-3 lg:gap-5 justify-center lg:justify-start">
              <motion.button
                onClick={() => router.push(ROUTES.TEXT_TO_IMAGE)}
                whileHover={{ scale: 1.05 }}
                className="px-6 py-3 lg:px-8 lg:py-4 h-10 lg:h-12 rounded-xl bg-primary text-black font-bold flex items-center gap-2 lg:gap-3 group transition-all text-sm lg:text-base"
              >
                {t('get_started') || 'Get Started'}
                <ArrowRight className="w-4 h-4 lg:w-5 lg:h-5 group-hover:translate-x-1 transition-transform" />
              </motion.button>
            </div>
          </div>

          <div className="w-full lg:w-1/2 h-full relative flex items-center justify-center">
            <motion.div className="relative w-full aspect-video sm:aspect-[4/3] lg:aspect-[14/11] rounded-2xl lg:rounded-border-radius overflow-hidden shadow-2xl flex-shrink-0">
              <Image
                src={getResolvedImageUrl(
                  (feature?.image_id as any)?.file_path || feature?.image_id,
                  '/images/landing/feature/text-to-image.png'
                )}
                alt={feature.title || 'Feature'}
                fill
                unoptimized
                className="object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent opacity-60" />
            </motion.div>
          </div>
        </div>
      </div>
    </motion.div>
  )
}
